import { Component, type ErrorInfo, type ReactNode } from 'react';

import { pt } from '../copy';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallback?: (err: Error, reset: () => void) => ReactNode;
};

type ErrorBoundaryState = {
  error: Error | null;
};

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('TMP error boundary caught:', error, info);
  }

  private readonly reset = () => {
    this.setState({ error: null });
  };

  override render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }

      return (
        <div className="grid h-full min-h-[320px] w-full place-items-center rounded-2xl border border-amber-400/20 bg-black/40 px-6 py-8 text-center text-white">
          <div className="space-y-4">
            <p className="text-sm font-semibold text-amber-100">{pt.viewerLoadError}</p>
            <p className="font-mono text-xs text-white/55">{this.state.error.message}</p>
            <button
              className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white px-4 py-2 text-sm font-semibold text-black transition hover:bg-white/90"
              type="button"
              onClick={this.reset}
            >
              {pt.retryFailed}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
